<template>
  <div>
    <b-card class="list-card monthly-att-card">
      <div class="gradient-line-wrapper"></div>

      <!-- Header -->
      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0">Monthly Attendance</h5>
          <small class="text-muted">Mark class-wise monthly attendance</small>
        </div>
      </div>

      <!-- ── GRID VIEW ─────────────────────────────────────────────────────── -->
      <template v-if="viewMode === 'grid'">
        <!-- Month / Year filters + Add button -->
        <b-row class="align-items-end">
          <b-col
            v-if="isHeadOffice"
            xl="3"
            lg="3"
            md="6"
            sm="6"
            cols="12"
            class="mb-1"
          >
            <label class="filter-label">Campus</label>
            <v-select
              v-model="selectedCampusID"
              :options="campuses"
              label="branch"
              :reduce="(c) => c.id"
              :clearable="false"
              placeholder="All Campuses"
              append-to-body
              class="filter-select"
              :disabled="gridLoading"
              @input="onGridFilterChange"
            />
          </b-col>

          <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
            <label class="filter-label"
              >Month <span class="text-danger">*</span></label
            >
            <v-select
              v-model="filters.month"
              :options="months"
              label="name"
              :reduce="(m) => m.id"
              :selectable="
                (opt) => filters.year < currentYear || opt.id <= currentMonth
              "
              :clearable="false"
              placeholder="Select month"
              append-to-body
              class="filter-select"
              :disabled="gridLoading"
              @input="onGridFilterChange"
            />
          </b-col>

          <b-col xl="3" lg="3" md="6" sm="6" cols="12" class="mb-1">
            <label class="filter-label"
              >Year <span class="text-danger">*</span></label
            >
            <v-select
              v-model="filters.year"
              :options="yearOptions"
              :selectable="
                (opt) =>
                  (opt == currentYear && filters.month <= currentMonth) ||
                  opt < currentYear
              "
              :clearable="false"
              placeholder="Select year"
              append-to-body
              class="filter-select"
              :disabled="gridLoading"
              @input="onGridFilterChange"
            />
          </b-col>

          <b-col cols="auto" class="mb-1 ml-auto d-flex" style="gap: 0.5rem">
            <b-button
              v-if="isHeadOffice"
              variant="outline-primary"
              class="action-btn-filter"
              :disabled="!filters.month || !filters.year || gridLoading"
              @click="openLockModal"
            >
              <feather-icon icon="LockIcon" size="16" class="mr-50" />
              Lock
            </b-button>
            <b-button
              variant="outline-primary"
              class="action-btn-filter"
              :disabled="!filters.month || !filters.year || gridLoading"
              @click="openStatusReport"
            >
              <feather-icon icon="BarChart2Icon" size="16" class="mr-50" />
              Report
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter"
              :disabled="!filters.month || !filters.year"
              @click="toggleAddPanel"
            >
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Add New
            </b-button>
          </b-col>
        </b-row>

        <!-- Inline add panel: class + section -->
        <div v-if="showAddPanel" class="add-panel mb-2">
          <div class="add-panel__body">
            <div class="add-panel__field">
              <label class="filter-label"
                >Class <span class="text-danger">*</span></label
              >
              <v-select
                v-model="addFilters.classID"
                :options="classOptions"
                label="name"
                :reduce="(c) => c.id"
                :clearable="false"
                placeholder="Select class"
                append-to-body
                class="filter-select"
                @input="onAddClassChange"
              />
            </div>
            <div class="add-panel__field">
              <label class="filter-label"
                >Section <span class="text-danger">*</span></label
              >
              <v-select
                v-model="addFilters.sectionID"
                :options="addSectionOptions"
                label="section"
                :reduce="(s) => s.id"
                :clearable="false"
                placeholder="Select section"
                append-to-body
                class="filter-select"
                :disabled="!addFilters.classID"
                @input="onAddSectionChange"
              />
            </div>
            <div class="add-panel__actions">
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="closeAddPanel"
              >
                Cancel
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                :disabled="!addFilters.classID || addFilters.sectionID === null"
                @click="confirmAdd"
              >
                <feather-icon icon="ArrowRightIcon" size="14" class="mr-50" />
                Open
              </b-button>
            </div>
          </div>
        </div>

        <!-- Grid loading -->
        <div v-if="gridLoading" class="text-center py-4">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1 mb-0">Loading…</p>
        </div>

        <!-- Grid prompt -->
        <div
          v-else-if="(!gridLoading && !filters.month) || !filters.year"
          class="text-center py-4 text-muted"
        >
          <feather-icon icon="FilterIcon" size="36" class="mb-1" />
          <p class="mb-0">Select month and year to view attendance records.</p>
        </div>

        <!-- Grid empty -->
        <div
          v-else-if="!gridLoading && gridRows.length === 0"
          class="text-center py-4 text-muted"
        >
          <feather-icon icon="InboxIcon" size="36" class="mb-1" />
          <p class="mb-0">
            No attendance records found for selected month and year.
          </p>
        </div>

        <!-- Grid table -->
        <template v-else>
          <div
            v-if="isHeadOffice && statsObj"
            class="d-flex mb-50"
            style="gap: 1rem"
          >
            <!-- Marked Card -->
            <div
              class="d-flex align-items-center p-50 rounded cursor-pointer stats-perc-card stats-marked"
            >
              <div
                class="d-flex justify-content-center align-items-center rounded bg-white stats-icon-box"
              >
                <feather-icon icon="CheckIcon" size="20" class="text-success" />
              </div>
              <div class="ml-1">
                <div
                  class="font-medium-2 font-weight-bold line-height-1 stats-marked__perc"
                >
                  {{ statsObj.markedPerc }} %
                </div>
                <div class="font-small-3 font-weight-bold stats-marked__label">
                  MARKED
                </div>
              </div>
            </div>
            <!-- Unmarked Card -->
            <div
              class="d-flex align-items-center p-50 rounded cursor-pointer stats-perc-card stats-unmarked"
              @click="showUnmarkList()"
            >
              <div
                class="d-flex justify-content-center align-items-center rounded bg-white stats-icon-box"
              >
                <feather-icon icon="XIcon" size="20" class="text-danger" />
              </div>
              <div class="ml-1">
                <div
                  class="font-medium-2 font-weight-bold line-height-1 stats-unmarked__perc"
                >
                  {{ statsObj.unmarkedPerc }} %
                </div>
                <div
                  class="font-small-3 font-weight-bold stats-unmarked__label"
                >
                  UNMARKED
                </div>
              </div>
            </div>
          </div>

          <div class="table-scroll-container">
            <div
              class="table-scrollbar-top"
              ref="topScrollbar"
              @scroll="syncScroll"
            >
              <div class="table-scrollbar-content"></div>
            </div>
            <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
              <b-table
                class="data-table monthly-att-table mt-0"
                :items="gridRows"
                :fields="computedGridFields"
                :responsive="false"
                hover
                show-empty
              >
                <template #cell(sno)="{ index }">
                  <span>{{ index + 1 }}</span>
                </template>

                <template #cell(classSection)="{ item }">
                  <span class="font-weight-bold">{{ item.className }}</span>
                  <span class="font-weight-bold" v-if="item.sectionName">
                    - {{ item.sectionName }}</span
                  >
                </template>

                <template #cell(campusName)="{ item }">
                  <span class="text-nowrap font-weight-bold">{{
                    item.branch || "—"
                  }}</span>
                </template>

                <template #cell(studentCount)="{ item }">
                  <b-badge variant="light-primary" class="font-weight-bold">
                    {{ item.studentCount }}
                  </b-badge>
                </template>

                <template #cell(actions)="{ item }">
                  <b-button
                    v-if="rights.edit"
                    variant="gradient-primary"
                    size="sm"
                    v-b-tooltip.hover.left
                    title="Edit Attendance"
                    class="action-btn-table"
                    :disabled="gridLoading || removingAttendance"
                    @click="openEdit(item)"
                  >
                    <feather-icon icon="EditIcon" size="14" />
                  </b-button>

                  <b-button
                    v-if="isHeadOffice && rights.delete"
                    variant="gradient-danger"
                    size="sm"
                    v-b-tooltip.hover.right
                    title="Delete Attendance"
                    class="action-btn-table ml-50"
                    :disabled="gridLoading || removingAttendance"
                    @click="deleteAttendance(item)"
                  >
                    <feather-icon icon="TrashIcon" size="14" />
                  </b-button>
                </template>
              </b-table>
            </div>
          </div>
        </template>
      </template>

      <!-- ── FORM VIEW ─────────────────────────────────────────────────────── -->
      <template v-else>
        <!-- Locked context bar -->
        <div class="context-bar mb-2">
          <div class="context-info font-weight-bold">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span>{{ monthName }} {{ filters.year }}</span>
            <span class="mx-1">·</span>
            <feather-icon
              icon="BookOpenIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span>{{ filters.className }}</span>
            <span v-if="filters.sectionName"> – {{ filters.sectionName }}</span>
          </div>
        </div>

        <!-- Total Days + Save / Cancel -->
        <div class="total-days-row mb-2">
          <div class="total-days-wrap">
            <label class="filter-label mb-0 mr-1">
              Total Working Days
              <span class="text-muted small">(max {{ daysInMonth }})</span>
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              v-model.number="totalDays"
              type="number"
              min="1"
              :max="daysInMonth"
              class="total-days-input"
              placeholder="Enter total days"
              @input="onTotalDaysChange"
            />
          </div>
          <div class="d-flex align-items-center ml-auto" style="gap: 0.5rem">
            <b-button
              variant="outline-secondary"
              class="action-btn-filter"
              :disabled="saving"
              @click="cancelEdit"
            >
              <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
              Back
            </b-button>
            <b-button
              variant="gradient-success"
              class="action-btn-filter"
              :disabled="saving || dataLoading || records.length == 0"
              @click="save"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
              Save
            </b-button>
          </div>
        </div>

        <!-- Form loading -->
        <div v-if="dataLoading" class="text-center py-4">
          <b-spinner variant="primary" />
          <p class="text-muted mt-1 mb-0">Loading attendance…</p>
        </div>

        <!-- Empty -->
        <div
          v-else-if="!dataLoading && records.length === 0"
          class="text-center py-4 text-muted"
        >
          <feather-icon icon="InboxIcon" size="36" class="mb-1" />
          <p class="mb-0">No students found.</p>
        </div>

        <!-- Students table -->
        <div v-else class="table-scroll-container">
          <div
            class="table-scrollbar-top"
            ref="topScrollbar"
            @scroll="syncScroll"
          >
            <div class="table-scrollbar-content"></div>
          </div>
          <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
            <b-table
              class="data-table monthly-att-table"
              :items="records"
              :fields="fields"
              :responsive="false"
              hover
              show-empty
            >
              <template #cell(sno)="{ index }">
                <span>{{ index + 1 }}</span>
              </template>

              <template #cell(student)="{ item }">
                <b-media vertical-align="center">
                  <template #aside>
                    <b-avatar
                      size="40"
                      :src="item.picture"
                      variant="light-primary"
                    />
                  </template>
                  <span class="font-weight-bold d-block text-nowrap">
                    {{ item.name || "—" }}
                  </span>
                  <small class="text-muted text-nowrap"
                    >FB Reg. No. {{ item.federalRegNo || "—" }}</small
                  >
                </b-media>
              </template>

              <template #cell(presentDays)="{ item }">
                <b-form-input
                  v-model.number="item.summary.presentDays"
                  type="number"
                  min="0"
                  :max="totalDays || daysInMonth"
                  class="present-days-input"
                  placeholder="0"
                  @input="clampPresentDays(item)"
                />
              </template>

              <template #cell(remarks)="{ item }">
                <b-form-textarea
                  rows="2"
                  size="sm"
                  v-model.trim="item.summary.attendanceComment"
                  placeholder="Enter comment"
                />
              </template>
            </b-table>
          </div>
        </div>
      </template>
    </b-card>

    <!-- Attendance status report (view on screen) -->
    <b-modal
      v-model="showReportModal"
      title="Attendance Status Report"
      size="xl"
      centered
      scrollable
      hide-footer
    >
      <b-row v-if="reportsFiltersVisible" class="align-items-end mb-2">
        <b-col
          v-if="isHeadOffice"
          xl="3"
          lg="3"
          md="6"
          sm="6"
          cols="12"
          class="mb-1"
        >
          <label class="filter-label">Campus</label>
          <v-select
            v-model="reportFilters.campusID"
            :options="campuses"
            label="branch"
            :reduce="(c) => c.id"
            :clearable="false"
            placeholder="All Campuses"
            class="filter-select"
            :disabled="reportLoading"
          />
        </b-col>

        <b-col
          cols="12"
          class="d-flex align-items-start flex-wrap"
          style="column-gap: 1rem"
        >
          <div>
            <label class="filter-label d-block"
              >Month <span class="text-danger">*</span></label
            >
            <div class="d-flex flex-wrap align-items-center">
              <b-button
                v-for="mon in months"
                :key="mon.id"
                class="btn-icon mr-50 mb-1"
                :variant="
                  reportFilters.month.includes(mon.id)
                    ? 'primary'
                    : 'outline-primary'
                "
                :disabled="reportLoading"
                @click="toggleReportMonth(mon.id)"
              >
                {{ mon.name.slice(0, 3) }}
              </b-button>
            </div>
          </div>
          <div style="width: 120px">
            <label class="filter-label"
              >Year <span class="text-danger">*</span></label
            >
            <v-select
              v-model="reportFilters.year"
              :options="yearOptions"
              :clearable="false"
              placeholder="Select year"
              class="filter-select"
              :disabled="reportLoading"
            />
          </div>
          <div class="mt-1">
            <b-button
              variant="primary"
              class="action-btn-filter"
              :disabled="isInvalidFilter || reportLoading"
              @click="applyReportFilters"
            >
              Show
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div v-if="reportLoading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="text-muted mt-1 mb-0">Loading report…</p>
      </div>

      <div
        v-else-if="reportRows.length === 0"
        class="text-center py-4 text-muted"
      >
        <feather-icon icon="InboxIcon" size="36" class="mb-1" />
        <p class="mb-0">No records found.</p>
      </div>

      <div v-else>
        <div
          class="report-meta mb-2 d-flex flex-wrap align-items-center justify-content-between"
        >
          <div class="d-flex flex-wrap align-items-center mb-50 mb-md-0">
            <b-button
              class="mr-75 btn-icon"
              variant="outline-secondary"
              size="sm"
              pill
              @click="openStatusReport"
            >
              <feather-icon icon="ChevronLeftIcon" size="16" />
            </b-button>

            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ reportMonthNamesShort }} {{ reportFilters.year }}</span
            >
            <template v-if="isHeadOffice && reportFilters.campusID">
              <span class="mx-1">·</span>
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-50 text-primary"
              />
              <span class="font-weight-bold">{{ selectedCampusLabel }}</span>
            </template>
            <template v-else-if="isHeadOffice">
              <span class="mx-1">·</span>
              <span class="">All Campuses</span>
            </template>
            <span class="mx-1">·</span>
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ reportRows.length }} student{{
                reportRows.length !== 1 ? "s" : ""
              }}</span
            >
          </div>
          <div
            v-if="isHeadOffice && reportRows.length > 0"
            class="d-flex flex-wrap align-items-center mb-50"
            style="gap: 0.5rem"
          >
            <b-button
              v-if="reportSelectedItems.length > 0"
              variant="gradient-success"
              size="sm"
              :disabled="reportStatusUpdating"
              @click="updateStatus"
            >
              <b-spinner v-if="reportStatusUpdating" small class="mr-50" />
              <feather-icon
                v-else
                icon="CheckCircleIcon"
                size="16"
                class="mr-50"
              />
              Update status
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="reportRows.length === 0"
              @click="printStatusReport"
            >
              <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
              Print
            </b-button>
            <b-button
              variant="outline-success"
              size="sm"
              class="ml-50"
              :disabled="reportRows.length === 0"
              @click="exportExcel"
            >
              <feather-icon icon="DownloadIcon" size="16" class="mr-50" />
              {{ $t("Excel") }}
            </b-button>
          </div>
        </div>

        <b-table
          ref="statusReportTable"
          class="data-table report-table"
          :items="reportRows"
          :fields="reportTableFields"
          hover
          responsive
          show-empty
          sticky-header="350px"
          :selectable="isHeadOffice ? true : false"
          :select-mode="'multi'"
          @row-selected="onRowSelect($event)"
        >
          <template #head(sno)>
            <div
              v-if="isHeadOffice"
              class="d-flex align-items-center justify-content-center text-nowrap"
              style="gap: 0.35rem"
            >
              <b-form-checkbox
                :checked="
                  reportSelectedItems.length > 0 &&
                  reportSelectedItems.length == reportRows.length
                "
                @change="setSelectAll"
                title="Select all with suggested status"
                class="mb-0"
              />
              <span>#</span>
            </div>
            <span v-else>#</span>
          </template>

          <template #cell(sno)="{ item, index }">
            <div
              v-if="isHeadOffice"
              class="d-flex align-items-center justify-content-center"
              style="gap: 0.35rem"
            >
              <b-form-checkbox
                :checked="reportSelectedItems.some((el) => el.sID == item.sID)"
                @change="(e) => setSelected(e, index)"
                class="mb-0"
              />
              <span class="text-muted">{{ index + 1 }}</span>
            </div>
            <span v-else class="text-muted">{{ index + 1 }}</span>
          </template>

          <template #cell(student)="{ item }">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="36"
                  :src="item.picture"
                  variant="light-primary"
                />
              </template>
              <span class="font-weight-bold d-block text-nowrap">{{
                item.name || "—"
              }}</span>
              <small v-if="isTcf" class="text-muted text-nowrap">
                FB Reg. No.
                {{
                  item.studentDetails
                    ? item.studentDetails.federalRegNo || "—"
                    : "—"
                }}
                <div>
                  <b-badge
                    :variant="
                      statusVariant(
                        item.studentDetails
                          ? item.studentDetails.studentStatus
                          : '—'
                      )
                    "
                  >
                    {{
                      item.studentDetails
                        ? item.studentDetails.studentStatus
                        : "—"
                    }}
                  </b-badge>
                </div>
              </small>
              <small v-else class="text-muted text-nowrap"
                >GR: {{ item.grNo || "—" }}</small
              >
            </b-media>
          </template>

          <template #cell(classSection)="{ item }">
            <span class="text-nowrap">
              {{ item.className }}
              <span v-if="item.sectionName"> – {{ item.sectionName }}</span>
            </span>
          </template>

          <template
            v-for="mid in reportTableDistinctMonths"
            v-slot:[reportMonthBreakdownSlotName(mid)]="{ item }"
          >
            <div v-if="getReportMonthlyBreakdownForMonth(item, mid)" class="">
              <div class="small font-weight-bold text-nowrap">
                {{
                  getReportMonthlyBreakdownForMonth(item, mid).presentDays
                }}/{{ getReportMonthlyBreakdownForMonth(item, mid).totalDays }}
              </div>
              <b-badge variant="light-primary"
                >{{
                  getReportMonthlyBreakdownForMonth(item, mid).percentage
                }}%</b-badge
              >
            </div>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(totalDays)="{ item }">
            <b-badge variant="light-secondary">{{ item.totalDays }}</b-badge>
          </template>

          <template #cell(presentDays)="{ item }">
            <b-badge variant="light-success">{{ item.presentDays }}</b-badge>
          </template>

          <template #cell(absentDays)="{ item }">
            <b-badge variant="light-danger">{{ item.absentDays }}</b-badge>
          </template>

          <template #cell(percentage)="{ item }">
            <span :class="percentageClass(item.percentage)">
              {{ item.percentage != null ? item.percentage + "%" : "—" }}
            </span>
          </template>

          <template #cell(docs)="{ item }">
            <div
              v-for="(dd, dInd) in item.uploadedDocs || []"
              :key="dInd"
              class="mb-25"
              @click.stop="dd.url && openFile(dd.url)"
            >
              <b-badge variant="light-success">
                {{ dd.docTitle }}
              </b-badge>
            </div>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              :variant="
                statusVariant(
                  item.studentDetails ? item.studentDetails.studentStatus : '—'
                )
              "
            >
              {{
                item.studentDetails ? item.studentDetails.studentStatus : "—"
              }}
            </b-badge>
          </template>

          <template #cell(suggestedStudentStatus)="{ item }">
            <b-badge :variant="statusVariant(item.suggestedStudentStatus)">
              {{ item.suggestedStudentStatus || "—" }}
            </b-badge>
          </template>

          <template #cell(statusToUpdate)="{ item }">
            <b-input-group>
              <b-form-input
                style="width: 100px"
                placeholder="Select status"
                size="sm"
                disabled
                v-model.trim="item.statusToUpdate"
              />
              <b-input-group-append>
                <b-dropdown
                  :disabled="reportLoading"
                  toggle-class="btn-icon p-25"
                  variant="outline-secondary"
                  class="remarks-dropdown"
                  size="sm"
                  right
                  no-caret
                >
                  <template #button-content>
                    <feather-icon icon="ChevronDownIcon" size="16" />
                  </template>
                  <b-dropdown-item
                    v-for="(st, stIndex) in statusOptions"
                    :key="stIndex"
                    :active="item.statusToUpdate == st.value"
                    @click="item.statusToUpdate = st.value"
                    link-class="py-50"
                  >
                    {{ st.text }}
                  </b-dropdown-item>
                </b-dropdown>
              </b-input-group-append>
            </b-input-group>
          </template>

          <template #cell(comment)="{ item }">
            <b-form-textarea
              rows="2"
              size="sm"
              v-model.trim="item.comment"
              placeholder="Enter comment"
            />
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- lock status -->
    <b-modal
      v-model="showLockModal"
      title="Attendance Lock Status"
      centered
      scrollable
      hide-footer
    >
      <div v-if="lockLoading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="text-muted mt-1 mb-0">Loading…</p>
      </div>

      <div
        v-else-if="lockRecords.length === 0"
        class="text-center py-4 text-muted"
      >
        <feather-icon icon="InboxIcon" size="36" class="mb-1" />
        <p class="mb-0">No records found.</p>
      </div>

      <div v-else>
        <div
          class="report-meta mb-2 d-flex flex-wrap align-items-center justify-content-between"
        >
          <div class="d-flex flex-wrap align-items-center mb-50 mb-md-0">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ monthName }} {{ reportFilters.year }}</span
            >
          </div>
          <div
            class="d-flex flex-wrap align-items-center mb-50"
            style="gap: 0.5rem"
          >
            <span class="font-small-2">Lock All</span>
            <b-form-checkbox
              v-model="allLockCheck"
              class="mb-0"
              @change="(e) => setLockAll(e)"
            />

            <b-button
              variant="gradient-success"
              size="sm"
              :disabled="locking"
              @click="updateLockStatus"
            >
              <b-spinner v-if="locking" small class="mr-50" />
              <feather-icon
                v-else
                icon="CheckCircleIcon"
                size="16"
                class="mr-50"
              />
              Save
            </b-button>
          </div>
        </div>

        <b-table
          ref="lockTable"
          class="data-table report-table"
          :items="lockRecords"
          :fields="lockFields"
          responsive
          show-empty
          sticky-header="450px"
        >
          <template #cell(sno)="{ index }">
            <span class="text-muted">{{ index + 1 }}</span>
          </template>

          <template #cell(campus)="{ item }">
            <b-badge variant="light-primary">{{ item.campus.name }}</b-badge>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              class="cursor-pointer"
              :variant="item.a.isLocked ? 'light-success' : 'light-danger'"
              :disabled="locking"
              @click="
                item.a.isLocked = !item.a.isLocked;
                item.isChanged = true;
              "
            >
              {{ item.a.isLocked ? "Locked" : "Unlocked" }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- stats view -->
    <b-modal
      v-model="showStatsModal"
      title="Unmarked Status Report"
      size="xl"
      centered
      scrollable
      hide-footer
    >
      <div v-if="statsRecords.length === 0" class="text-center py-4 text-muted">
        <feather-icon icon="InboxIcon" size="36" class="mb-1" />
        <p class="mb-0">No records found.</p>
      </div>

      <div v-else>
        <div
          class="report-meta mb-2 d-flex flex-wrap align-items-center justify-content-between"
        >
          <div class="d-flex flex-wrap align-items-center mb-50 mb-md-0">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ monthName }} {{ filters.year }}</span
            >
            <template v-if="selectedCampusID">
              <span class="mx-1">·</span>
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-50 text-primary"
              />
              <span class="font-weight-bold">{{ gridCampusLabel }}</span>
            </template>
            <span class="mx-1">·</span>
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ statsRecords.length }} student{{
                statsRecords.length !== 1 ? "s" : ""
              }}</span
            >
          </div>
        </div>

        <b-table
          ref="statusReportTable"
          class="data-table report-table"
          :items="statsRecords"
          :fields="statsTableFields"
          hover
          responsive
          show-empty
          sticky-header="350px"
        >
          <template #cell(sno)="{ item, index }">
            <span>{{ index + 1 }}</span>
          </template>

          <template #cell(student)="{ item }">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="36"
                  :src="item.student.picture"
                  variant="light-primary"
                />
              </template>
              <span class="font-weight-bold d-block text-nowrap">{{
                item.student.name || "—"
              }}</span>
              <small v-if="isTcf" class="text-muted text-nowrap">
                FB Reg. No.
                {{
                  item.stdDetails ? item.stdDetails.federalRegNo || "—" : "—"
                }}
              </small>
              <small v-else class="text-muted text-nowrap"
                >GR: {{ item.student.grNo || "—" }}</small
              >
            </b-media>
          </template>

          <template #cell(father_name)="{ item }">
            {{ item.student.father_name }}
          </template>

          <template #cell(campus)="{ item }">
            <span class="text-nowrap font-weight-bold">{{
              item.meta.campusName || "—"
            }}</span>
          </template>

          <template #cell(class)="{ item }">
            <span class="text-nowrap font-weight-bold">
              {{ item.meta.className }} - {{ item.meta.sectionName }}</span
            >
          </template>
        </b-table>
      </div>
    </b-modal>

    <!-- campus wise stats view -->
    <b-modal
      v-model="showCampusStatsModal"
      title="Campus Wise Unmarked Attendance Summary"
      size="xl"
      centered
      scrollable
      hide-footer
      modal-class="campus-stats-modal"
      dialog-class="campus-stats-dialog"
    >
      <div v-if="campusStatsLoading" class="text-center py-4">
        <b-spinner variant="primary" label="Loading..." />
        <p class="mt-1 text-muted">Loading campus statistics...</p>
      </div>

      <div
        v-else-if="campusStatsList.length === 0"
        class="text-center py-4 text-muted"
      >
        <feather-icon icon="InboxIcon" size="36" class="mb-1" />
        <p class="mb-0">No records found.</p>
      </div>

      <div v-else>
        <div
          class="report-meta mb-2 d-flex flex-wrap align-items-center justify-content-between"
        >
          <div class="d-flex flex-wrap align-items-center mb-50 mb-md-0">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ monthName }} {{ filters.year }}</span
            >
            <template v-if="selectedCampusID">
              <span class="mx-1">·</span>
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-50 text-primary"
              />
              <span class="font-weight-bold">{{ gridCampusLabel }}</span>
            </template>
            <span class="mx-1">·</span>
            <feather-icon
              icon="HomeIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ campusStatsList.length }} Campus{{
                campusStatsList.length !== 1 ? "es" : ""
              }}</span
            >
            <span class="mx-1">·</span>
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="mr-50 text-primary"
            />
            <span class="font-weight-bold"
              >{{ totalCampusUnmarkedCount }} Unmarked Student{{
                totalCampusUnmarkedCount !== 1 ? "s" : ""
              }}</span
            >
          </div>

          <b-button
            variant="gradient-success"
            size="sm"
            class="action-btn-table"
            @click="exportAllCampusUnmarkedExcel"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
            {{ $t("Export All") }}
          </b-button>
        </div>

        <b-table
          class="data-table report-table campus-stats-table"
          :items="campusStatsList"
          :fields="campusStatsFields"
          hover
          responsive
          show-empty
          sticky-header="55vh"
        >
          <template #cell(sno)="{ index }">
            <span>{{ index + 1 }}</span>
          </template>

          <template #cell(campus)="{ item }">
            <span class="font-weight-bold text-nowrap">{{
              item.branch || item.campusName || "—"
            }}</span>
          </template>

          <template #cell(totalActiveStudents)="{ item }">
            <b-badge variant="light-primary" class="font-weight-bold">
              {{ item.totalActiveStudents }}
            </b-badge>
          </template>

          <template #cell(marked)="{ item }">
            <b-badge variant="light-success" class="font-weight-bold">
              {{ item.markedCount }} ({{ item.markedPerc }}%)
            </b-badge>
          </template>

          <template #cell(unmarked)="{ item }">
            <b-badge variant="light-danger" class="font-weight-bold">
              {{ item.unmarkedCount }} ({{ item.unmarkedPerc }}%)
            </b-badge>
          </template>

          <template #cell(actions)="row">
            <div
              class="d-flex align-items-center justify-content-center"
              style="gap: 0.5rem"
            >
              <b-button
                variant="gradient-primary"
                size="sm"
                class="action-btn-table"
                @click="row.toggleDetails"
                :disabled="
                  !row.item.unmarkedStds || row.item.unmarkedStds.length === 0
                "
              >
                <feather-icon
                  :icon="row.detailsShowing ? 'ChevronUpIcon' : 'EyeIcon'"
                  size="14"
                  class="mr-50"
                />
                {{ row.detailsShowing ? $t("Hide") : $t("View") }}
              </b-button>

              <b-button
                variant="gradient-success"
                size="sm"
                class="action-btn-table"
                @click="exportCampusUnmarkedExcel(row.item)"
                :disabled="
                  !row.item.unmarkedStds || row.item.unmarkedStds.length === 0
                "
              >
                <feather-icon icon="DownloadIcon" size="14" />
              </b-button>
            </div>
          </template>

          <template #row-details="row">
            <b-card no-body class="p-1 mb-0 border shadow-none bg-light">
              <div
                class="d-flex align-items-center justify-content-between mb-1"
              >
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="UsersIcon"
                    size="16"
                    class="mr-50 text-danger"
                  />
                  <span class="font-weight-bold">
                    Unmarked Students —
                    {{ row.item.branch || row.item.campusName }} ({{
                      (row.item.unmarkedStds || []).length
                    }})
                  </span>
                </div>
              </div>

              <b-table
                class="data-table report-table mb-0 mt-0 bg-white"
                :items="row.item.unmarkedStds || []"
                :fields="unmarkedStdFields"
                hover
                responsive
                show-empty
                small
                sticky-header="300px"
              >
                <template #cell(sno)="{ index }">
                  <span>{{ index + 1 }}</span>
                </template>

                <template #cell(student)="{ item }">
                  <b-media vertical-align="center">
                    <template #aside>
                      <b-avatar
                        size="36"
                        :src="item.student ? item.student.picture : null"
                        variant="light-primary"
                      />
                    </template>
                    <span class="font-weight-bold d-block text-nowrap">{{
                      item.student ? item.student.name || "—" : "—"
                    }}</span>
                    <small v-if="isTcf" class="text-muted text-nowrap">
                      FB Reg. No.
                      {{
                        item.stdDetails
                          ? item.stdDetails.federalRegNo || "—"
                          : "—"
                      }}
                    </small>
                    <small v-else class="text-muted text-nowrap"
                      >GR:
                      {{
                        item.student ? item.student.grNo || "—" : "—"
                      }}</small
                    >
                  </b-media>
                </template>

                <template #cell(father_name)="{ item }">
                  {{ item.student ? item.student.father_name : "—" }}
                </template>

                <template #cell(campus)="{ item }">
                  <span class="text-nowrap font-weight-bold">{{
                    item.meta
                      ? item.meta.campusName || "—"
                      : row.item.branch || row.item.campusName || "—"
                  }}</span>
                </template>

                <template #cell(class)="{ item }">
                  <span class="text-nowrap font-weight-bold">
                    {{ item.meta ? item.meta.className : "" }} -
                    {{ item.meta ? item.meta.sectionName : "" }}</span
                  >
                </template>
              </b-table>
            </b-card>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormCheckbox,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BAvatar,
  BMedia,
  BDropdown,
  BDropdownItem,
  BInputGroupAppend,
  BInputGroupPrepend,
  BInputGroup,
  BFormTextarea,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";
import * as XLSX from "xlsx";

const yearValue = new Date().getFullYear();
const monthValue = new Date().getMonth() + 1;

export default {
  name: "MonthlyAttendance",
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BAvatar,
    BMedia,
    BDropdown,
    BDropdownItem,
    BInputGroupAppend,
    BInputGroupPrepend,
    BInputGroup,
    BFormTextarea,
    vSelect,
  },
  directives: { "b-tooltip": VBTooltip },
  data() {
    return {
      rights: {},
      reportDomain: this.$store.state.report.replace(/_/g, ""),

      // View mode: 'grid' | 'form'
      viewMode: "grid",

      // Grid (LoadGrid) state
      gridRows: [],
      gridLoading: false,
      gridFields: [],

      // Filters
      filters: {
        classID: null,
        sectionID: null,
        className: "",
        sectionName: "",
        campusID: this.$store.state.userData.cId,
        month: monthValue,
        year: yearValue,
      },

      currentYear: yearValue,
      currentMonth: monthValue,

      months: [
        { id: 1, name: "January" },
        { id: 2, name: "February" },
        { id: 3, name: "March" },
        { id: 4, name: "April" },
        { id: 5, name: "May" },
        { id: 6, name: "June" },
        { id: 7, name: "July" },
        { id: 8, name: "August" },
        { id: 9, name: "September" },
        { id: 10, name: "October" },
        { id: 11, name: "November" },
        { id: 12, name: "December" },
      ],

      yearOptions: Array.from({ length: 6 }, (_, i) => yearValue - 2 + i),

      // Status report modal (view on screen)
      showReportModal: false,
      reportLoading: false,
      reportRows: [],
      reportSelectedItems: [],
      reportStatusUpdating: false,
      reportsFiltersVisible: true,
      reportFilters: {
        campusID: this.$store.state.userData.cId,
        month: [monthValue],
        year: yearValue,
      },

      // Head office
      isHeadOffice: null,
      campuses: [],
      selectedCampusID: this.$store.state.userData.cId,

      // Add panel
      showAddPanel: false,
      addFilters: {
        classID: null,
        sectionID: null,
        className: "",
        sectionName: "",
      },
      classes: [],
      classOptions: [],
      addSectionOptions: [],

      // Attendance data
      records: [],
      totalDays: null,
      dataLoading: false,
      saving: false,

      fields: [
        { key: "sno", label: "#", thClass: "w-sn" },
        { key: "student", label: "Student", sortable: false },
        { key: "father_name", label: "Father Name", sortable: false },
        { key: "presentDays", label: "Present Days", sortable: false },
        { key: "remarks", label: "Remarks", sortable: false },
      ],

      statusOptions: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
        { text: "On Probation", value: "on-probation" },
      ],

      attLocked: false,
      allLockCheck: false,
      showLockModal: false,
      lockLoading: false,
      lockRecords: [],
      lockFields: [
        { key: "sno", label: "#", thClass: "w-sn" },
        { key: "campus", label: "Campus" },
        { key: "status", label: "Status" },
      ],
      locking: false,
      removingAttendance: false,
      statsObj: null,
      statsLoading: false,
      showStatsModal: false,
      statsRecords: [],
      statsTableFields: [
        { key: "sno", label: "#" },
        { key: "student", label: "Student" },
        { key: "father_name", label: "Father Name" },
        // { key: "campus", label: "Campus" },
        { key: "class", label: "Class" },
      ],
      showCampusStatsModal: false,
      campusStatsLoading: false,
      campusStatsList: [],
      campusStatsFields: [
        {
          key: "sno",
          label: "#",
          thClass: "w-sn text-center",
          tdClass: "text-center",
        },
        {
          key: "campus",
          label: "Campus",
          thStyle: { minWidth: "180px" },
        },
        {
          key: "totalActiveStudents",
          label: "Total Students",
          thClass: "text-center",
          tdClass: "text-center",
          thStyle: { minWidth: "110px" },
        },
        {
          key: "marked",
          label: "Marked",
          thClass: "text-center",
          tdClass: "text-center",
          thStyle: { minWidth: "120px" },
        },
        {
          key: "unmarked",
          label: "Unmarked",
          thClass: "text-center",
          tdClass: "text-center",
          thStyle: { minWidth: "120px" },
        },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
          thStyle: { minWidth: "190px" },
        },
      ],
      unmarkedStdFields: [
        {
          key: "sno",
          label: "#",
          thClass: "w-sn text-center",
          tdClass: "text-center",
        },
        {
          key: "student",
          label: "Student",
          thStyle: { minWidth: "160px" },
        },
        {
          key: "father_name",
          label: "Father Name",
          thStyle: { minWidth: "140px" },
        },
        {
          key: "class",
          label: "Class",
          thStyle: { minWidth: "130px" },
        },
      ],
      loadedTypes: [],
    };
  },
  computed: {
    totalCampusUnmarkedCount() {
      return (this.campusStatsList || []).reduce(
        (acc, c) =>
          acc +
          (c.unmarkedCount != null
            ? Number(c.unmarkedCount)
            : c.unmarkedStds
            ? c.unmarkedStds.length
            : 0),
        0
      );
    },
    isTcf() {
      return ["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain);
    },
    computedGridFields() {
      const base = [
        { key: "sno", label: "#", thClass: "w-sn" },
        { key: "classSection", label: "Class / Section", sortable: false },
        { key: "studentCount", label: "Students", sortable: false },
        { key: "actions", label: "Actions" },
      ];
      if (this.isHeadOffice) {
        base.splice(1, 0, {
          key: "campusName",
          label: "Campus",
          sortable: false,
        });
      }
      return base;
    },
    daysInMonth() {
      if (!this.filters.month || !this.filters.year) return 31;
      return new Date(this.filters.year, this.filters.month, 0).getDate();
    },
    monthName() {
      const m = this.months.find((x) => x.id === this.filters.month);
      return m ? m.name : "";
    },
    gridCampusLabel() {
      const c = this.campuses.find((x) => x.id === this.selectedCampusID);
      return c ? c.branch : "";
    },
    reportMonthNamesShort() {
      const ids = this.reportFilters.month;
      if (!ids || ids.length === 0) return "";
      return [...ids]
        .sort((a, b) => a - b)
        .map((id) => {
          const m = this.months.find((x) => x.id === id);
          return m ? m.name.slice(0, 3) : "";
        })
        .filter(Boolean)
        .join(", ");
    },
    /** Distinct calendar months (1–12) appearing in status report monthlyBreakdown, sorted ascending */
    reportTableDistinctMonths() {
      const set = new Set();
      const rows = this.reportRows || [];
      for (const row of rows) {
        const arr = row.monthlyBreakdown;
        if (!Array.isArray(arr)) continue;
        for (const b of arr) {
          if (b != null && b.month != null) set.add(Number(b.month));
        }
      }
      return [...set].sort((a, b) => a - b);
    },
    isInvalidFilter() {
      return (
        this.reportFilters.month.length == 0 ||
        !this.reportFilters.year ||
        !this.reportFilters.campusID
      );
    },
    selectedCampusLabel() {
      const c = this.campuses.find((x) => x.id === this.reportFilters.campusID);
      return c ? c.branch : "";
    },
    statusReportPrintPeriodLabel() {
      const y = this.filters.year;
      return [this.monthName, y].filter(Boolean).join(" ");
    },
    statusReportPrintCampusLabel() {
      if (this.isHeadOffice) {
        return this.reportFilters.campusID
          ? this.selectedCampusLabel
          : "All Campuses";
      }
      return this.$store.state.userData?.campusName || "";
    },
    reportTableFields() {
      const fields = [];
      fields.push(
        {
          key: "sno",
          label: "#",
          thClass: this.isHeadOffice
            ? "text-center report-status-sno-th"
            : "w-sn",
          tdClass: this.isHeadOffice ? "text-center" : "",
        },
        { key: "student", label: "Student", sortable: false },
        { key: "classSection", label: "Class / Section", sortable: false }
      );
      for (const mid of this.reportTableDistinctMonths) {
        const m = this.months.find((x) => x.id === mid);
        const label = m ? m.name.slice(0, 3) : String(mid);
        fields.push({
          key: `mdb_${mid}`,
          label,
          sortable: false,
          thClass: "text-center text-nowrap",
          tdClass: "text-center text-nowrap",
        });
      }
      fields.push(
        { key: "totalDays", label: "Total", sortable: false },
        { key: "presentDays", label: "Present", sortable: false },
        { key: "absentDays", label: "Absent", sortable: false },
        { key: "percentage", label: "%", sortable: false }
      );
      if (this.isHeadOffice) {
        // fields.push({ key: "status", label: "Status", sortable: false });
        fields.push({
          key: "docs",
          label: "Approved Docs",
          sortable: false,
        });
        fields.push({
          key: "suggestedStudentStatus",
          label: "Suggested status",
          sortable: false,
        });
        fields.push({
          key: "statusToUpdate",
          label: "Status to update",
          sortable: false,
        });
        fields.push({ key: "comment", label: "Comment", sortable: false });
      }
      return fields;
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.LoadSettings();
      this.LoadClasses();
      if (this.filters.month && this.filters.year) this.loadGrid();
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    openFile(url) {
      if (url) window.open(url, "_blank");
    },

    // ── Scrollbar sync ──────────────────────────────────────────────────────

    updateScrollbarWidth() {
      this.$nextTick(() => {
        const tw = this.$refs.tableWrapper;
        const ts = this.$refs.topScrollbar;
        const tc = ts && ts.querySelector(".table-scrollbar-content");
        if (tw && tc) {
          tc.style.width = `${tw.scrollWidth}px`;
          ts.scrollLeft = tw.scrollLeft;
        }
      });
    },

    syncScroll(event) {
      const tw = this.$refs.tableWrapper;
      const ts = this.$refs.topScrollbar;
      if (!tw || !ts) return;
      if (event.target === tw) ts.scrollLeft = tw.scrollLeft;
      else if (event.target === ts) tw.scrollLeft = ts.scrollLeft;
    },

    toggleReportMonth(monthId) {
      if (this.reportLoading) return;
      const arr = this.reportFilters.month || [];
      if (arr.includes(monthId)) {
        if (arr.length <= 1) return;
        this.reportFilters.month = arr.filter((m) => m !== monthId);
      } else {
        this.reportFilters.month = [...arr, monthId];
      }
    },

    reportMonthBreakdownSlotName(monthId) {
      return `cell(mdb_${monthId})`;
    },
    /** Single month row from student row.monthlyBreakdown for status report columns */
    getReportMonthlyBreakdownForMonth(row, monthNum) {
      const arr = row && row.monthlyBreakdown;
      if (!Array.isArray(arr)) return null;
      const n = Number(monthNum);
      return arr.find((b) => b != null && Number(b.month) === n) || null;
    },

    printStatusReport() {
      if (
        this.gridLoading ||
        !this.reportFilters.month ||
        !this.reportFilters.month.length ||
        this.reportFilters.year == null ||
        this.reportFilters.year === "" ||
        this.reportRows.length === 0
      )
        return;
      const campusID =
        this.reportFilters.campusID || this.$store.state.userData.cId;
      const monthsCsv = [...this.reportFilters.month]
        .sort((a, b) => a - b)
        .join(",");
      const periodLabel =
        `${this.reportMonthNamesShort} ${this.reportFilters.year}`.trim();
      const routeData = this.$router.resolve({
        name: "monthly-attendance-status-report-print",
        query: {
          db: this.$store.state.userData.db,
          campusID: String(campusID),
          month: monthsCsv,
          year: String(this.reportFilters.year),
          periodLabel,
          campusLabel: this.statusReportPrintCampusLabel,
        },
      });
      window.open(routeData.href, "_blank", "noopener,noreferrer");
    },

    // ========= OLD ===============
    // printStatusReport() {
    //   if (!this.filters.month || !this.filters.year || this.gridLoading) return;
    //   const campusID = this.selectedCampusID || this.$store.state.userData.cId;
    //   const routeData = this.$router.resolve({
    //     name: "monthly-attendance-status-report-print",
    //     query: {
    //       db: this.$store.state.userData.db,
    //       campusID: String(campusID),
    //       month: String(this.filters.month),
    //       year: String(this.filters.year),
    //       periodLabel: this.statusReportPrintPeriodLabel,
    //       campusLabel: this.statusReportPrintCampusLabel,
    //     },
    //   });
    //   window.open(routeData.href, "_blank", "noopener,noreferrer");
    // },

    exportExcel() {
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = `attendance-summary-${stamp}.xlsx`;

      const kFederal = this.$t("Federal Board Registration No");
      const kStudent = this.$t("Student Name");
      const kFather = this.$t("Father Name");
      const kClass = this.$t("Class");
      const kPerc = this.$t("Percentage");
      const kSchCat = this.$t("Scholarship Category");
      const kSch = this.$t("Scholarship Status");
      const kStat = this.$t("Student Status");
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";
      const kComment = "Comment";

      const expData = (this.reportRows || []).map((item) => {
        const st = item.student || {};
        const det = item.studentDetails || {};
        const monthColumns = {};
        const upDocs = item.uploadedDocs || [];

        this.reportTableDistinctMonths.forEach((mid) => {
          const m = this.months.find((x) => x.id === mid);
          const label = m ? m.name.slice(0, 3) : String(mid);
          const monthRecord = this.getReportMonthlyBreakdownForMonth(item, mid);
          monthColumns[label] = monthRecord ? monthRecord.percentage : "-";
        });

        // Construct the main row object
        const row = {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kClass]: item.className + " - " + item.sectionName || "",
          // Spreads the dynamic month columns
          ...monthColumns,
          [kPerc]: item.percentage != null ? `${item.percentage}` : "",
          [kSchCat]: det.scholarshipCategory || "",
          [kSch]: det.scholarshipStatus || "",
          [kStat]: det.studentStatus || "",
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
          [kComment]: item.comment || " ",
        };

        // Add dynamic document title columns using loadedTypes
        if (this.loadedTypes.length > 0) {
          this.loadedTypes.forEach((el) => {
            row[el.title] =
              upDocs.find((dItem) => dItem.typeID == el.id)?.url || "";
          });
        }

        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      XLSX.writeFile(workbook, fname);
    },

    openLockModal() {
      this.lockRecords = [];
      this.showLockModal = true;
      this.allLockCheck = false;
      this.LoadLockStatus();
    },
    setLockAll(e) {
      if (e) {
        this.lockRecords.forEach((el) => {
          el.a.isLocked = true;
          el.isChanged = true;
        });
      }
    },
    async updateLockStatus() {
      const payload = this.lockRecords
        .filter((el) => el.isChanged)
        .map((el) => el.a);

      // console.log(payload);

      if (payload.length > 0) {
        this.locking = true;
        const res = await this.post({
          url:
            this.$store.state.domain +
            "AttendanceSummaryLock/Save?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body: payload,
          context: this,
          message: "Lock status updated.",
          token: this.$store.state.userData.token,
        });

        if (res) {
          this.LoadLockStatus();
        }
        this.locking = false;
      }
    },
    async LoadLockStatus(toCheck) {
      this.lockLoading = true;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "AttendanceSummaryLock/GetLockStatus?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          (this.selectedCampusID || this.$store.state.userData.cId) +
          "&month=" +
          this.filters.month +
          "&year=" +
          this.filters.year +
          "&userID=" +
          this.$store.state.userData.userID,
        token: this.$store.state.userData.token,
        returnData: true,
      });
      // console.log(res);
      let rows = [];
      if (Array.isArray(res)) rows = res;
      else if (res && Array.isArray(res.rows)) rows = res.rows;
      this.lockRecords = rows;
      this.lockLoading = false;

      if (!this.isHeadOffice && toCheck) {
        // console.log("lock check");
        const checkLock = this.lockRecords.find(
          (el) => el.campus.id == this.$store.state.userData.cId
        );
        // console.log(checkLock);
        this.attLocked = checkLock ? checkLock.a.isLocked : false;
      }
      // console.log("att lock: ",this.attLocked);
    },

    async openStatusReport() {
      this.reportRows = [];
      this.reportSelectedItems = [];
      this.showReportModal = true;
      this.reportsFiltersVisible = true;
    },
    async applyReportFilters() {
      // console.log(this.reportFilters);
      if (this.isInvalidFilter) return;
      this.reportLoading = true;
      try {
        await this.fetchStatusReportModalRows();
      } catch (e) {
        console.error("Load status report error:", e);
        this.$bvToast.toast("Failed to load status report.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.reportLoading = false;
      }
    },

    /** Shared GET for the attendance status report modal (used on open ansd after updates). */
    async fetchStatusReportModalRows() {
      const res = await this.get({
        url:
          this.$store.state.domain +
          "AttendanceSummary/LoadStatusReportMultiMonth?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          (this.reportFilters.campusID || this.$store.state.userData.cId) +
          "&months=" +
          this.reportFilters.month.join(",") +
          "&year=" +
          this.reportFilters.year,
        token: this.$store.state.userData.token,
        returnData: true,
      });
      // console.log(res);
      let rows = [];
      if (Array.isArray(res)) rows = res;
      else if (res && Array.isArray(res.data)) rows = res.data;
      this.reportRows = rows;
      if (rows.length > 0) this.reportsFiltersVisible = false;
      this.loadedTypes = res?.docTypes || [];
    },

    setSelectAll(e) {
      if (e) {
        this.$refs.statusReportTable.selectAllRows();
      } else {
        this.$refs.statusReportTable.clearSelected();
      }
    },
    setSelected(e, ind) {
      if (e) {
        this.$refs.statusReportTable.selectRow(ind);
      } else {
        this.$refs.statusReportTable.unselectRow(ind);
      }
    },
    onRowSelect(ch) {
      // console.log(ch);
      this.reportSelectedItems = ch.reduce((acc, c) => {
        acc.push({ sID: c.sID, status: c.statusToUpdate, comment: c.comment });
        return acc;
      }, []);
    },

    async updateStatus() {
      if (!this.isHeadOffice || this.reportSelectedItems.length === 0) return;

      if (this.reportSelectedItems.length === 0) {
        this.$bvToast.toast(
          "No valid selection with a suggested status to update.",
          { title: "Nothing to update", variant: "warning", solid: true }
        );
        return;
      }

      let pData = this.reportRows.filter((row) =>
        this.reportSelectedItems.some((el) => el.sID == row.sID)
      );
      const payload = pData.map((el) => ({
        sID: el.sID,
        status: el.statusToUpdate || el.suggestedStudentStatus,
        comment: el.comment || "",
      }));
      // console.log(payload);

      const ok = await this.$bvModal.msgBoxConfirm(
        `Update current status to the suggested status for ${this.reportSelectedItems.length} student(s)? You won't be able to revert this!`,
        {
          title: "Confirm status update",
          okTitle: "Yes, update",
          okVariant: "primary",
          cancelTitle: "Cancel",
          cancelVariant: "outline-secondary",
          centered: true,
        }
      );
      if (!ok) return;

      this.reportStatusUpdating = true;
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "AttendanceSummary/UpdateStudentStatus?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID +
            "&months=" +
            this.reportFilters.month.join(",") +
            "&year=" +
            this.reportFilters.year,
          body: payload,
          context: this,
          message: "Student status updated.",
          token: this.$store.state.userData.token,
        });

        if (res) {
          this.$refs.statusReportTable.clearSelected();
          await this.reloadStatusReportModalRows();
        }
      } catch (e) {
        console.error("UpdateStudentStatus error:", e);
      } finally {
        this.reportStatusUpdating = false;
      }
    },

    async reloadStatusReportModalRows() {
      this.reportLoading = true;
      try {
        await this.fetchStatusReportModalRows();
      } catch (e) {
        console.error("Reload status report error:", e);
        this.$bvToast.toast("Failed to refresh report.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.reportLoading = false;
      }
    },

    percentageClass(pct) {
      if (pct == null) return "text-muted";
      if (pct < 55) return "text-danger font-weight-bold";
      if (pct <= 74) return "text-warning font-weight-bold";
      return "text-success font-weight-bold";
    },

    statusVariant(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s === "active") return "light-success";
      if (s === "in-active" || s === "inactive") return "light-danger";
      if (s === "on-probation" || s === "probation") return "light-warning";
      return "secondary";
    },

    // ── Settings ─────────────────────────────────────────────────────────────

    async LoadSettings() {
      const res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.isHeadOffice = res && res.find((el) => el.key == "head_office");
      if (this.isHeadOffice) this.loadCampuses();
    },

    async loadCampuses() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.campuses = Array.isArray(res) ? res : (res && res.data) || [];
      } catch (e) {
        console.error("Load campuses error:", e);
      }
    },

    // ── Classes ─────────────────────────────────────────────────────────────

    async LoadClasses(campusID) {
      const cID = campusID || this.$store.state.userData.cId;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            cID +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        let arr = [];
        if (Array.isArray(res)) arr = res;
        else if (res && Array.isArray(res.data)) arr = res.data;
        this.classes = arr;
        this.classOptions = arr.map((el) => el.cls || el);
      } catch (e) {
        console.error("Load classes error:", e);
      }
    },

    // ── Add panel ────────────────────────────────────────────────────────────

    toggleAddPanel() {
      if (this.attLocked) {
        this.$bvToast.toast(
          "Attendance has been locked. Please contact Management",
          {
            title: "Attendance Locked",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      this.showAddPanel = !this.showAddPanel;
      if (this.showAddPanel) {
        this.resetAddFilters();
        // If head office, reload classes for the selected campus
        if (this.isHeadOffice) {
          this.LoadClasses(
            this.selectedCampusID || this.$store.state.userData.cId
          );
        }
      } else {
        this.resetAddFilters();
      }
    },

    closeAddPanel() {
      this.showAddPanel = false;
      this.resetAddFilters();
    },

    resetAddFilters() {
      this.addFilters = {
        classID: null,
        sectionID: null,
        className: "",
        sectionName: "",
      };
      this.addSectionOptions = [];
    },

    onAddClassChange() {
      this.addFilters.sectionID = null;
      this.addFilters.sectionName = "";
      this.addSectionOptions = [];
      if (!this.addFilters.classID) return;
      const found = this.classes.find(
        (el) => (el.cls || el).id === this.addFilters.classID
      );
      const cls = found ? found.cls || found : null;
      if (cls) this.addFilters.className = cls.name || "";
      if (found && found.sections && found.sections.length > 0) {
        this.addSectionOptions = [...found.sections];
      }
    },

    onAddSectionChange() {
      const sec = this.addSectionOptions.find(
        (s) => s.id === this.addFilters.sectionID
      );
      this.addFilters.sectionName = sec ? sec.section : "";
    },

    confirmAdd() {
      if (!this.addFilters.classID || this.addFilters.sectionID === null)
        return;
      const row = {
        classID: this.addFilters.classID,
        sectionID: this.addFilters.sectionID,
        className: this.addFilters.className,
        sectionName: this.addFilters.sectionName,
        campusID: this.isHeadOffice
          ? this.selectedCampusID || this.$store.state.userData.cId
          : this.$store.state.userData.cId,
      };
      this.closeAddPanel();
      this.openEdit(row);
    },

    // ── Grid ────────────────────────────────────────────────────────────────

    onGridFilterChange() {
      this.gridRows = [];
      this.closeAddPanel();
      if (this.filters.month && this.filters.year) this.loadGrid();
    },

    async loadGrid() {
      this.LoadLockStatus("check");
      this.LoadStats();
      this.gridLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "AttendanceSummary/LoadGrid?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            (this.selectedCampusID || this.$store.state.userData.cId) +
            "&month=" +
            this.filters.month +
            "&year=" +
            this.filters.year,
          token: this.$store.state.userData.token,
          returnData: true,
        });
        let rows = [];
        if (Array.isArray(res)) rows = res;
        else if (res && Array.isArray(res.data)) rows = res.data;
        this.gridRows = rows;
        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error("Load grid error:", e);
        this.$bvToast.toast("Failed to load attendance grid.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.gridLoading = false;
      }
    },

    async openEdit(row) {
      if (this.attLocked) {
        this.$bvToast.toast(
          "Attendance has been locked. Please contact Management",
          {
            title: "Attendance Locked",
            variant: "warning",
            solid: true,
          }
        );
        return;
      }
      this.filters.classID = row.classID;
      this.filters.sectionID = row.sectionID;
      this.filters.className = row.className;
      this.filters.sectionName = row.sectionName;
      this.filters.campusID = row.campusID || this.$store.state.userData.cId;
      this.records = [];
      this.totalDays = null;
      this.viewMode = "form";
      await this.loadData();
    },

    // ── Attendance data ─────────────────────────────────────────────────────

    async loadData() {
      this.dataLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "AttendanceSummary/LoadData?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            (this.filters.campusID || this.$store.state.userData.cId) +
            "&classID=" +
            this.filters.classID +
            "&sectionID=" +
            (this.filters.sectionID || 0) +
            "&month=" +
            this.filters.month +
            "&year=" +
            this.filters.year,
          token: this.$store.state.userData.token,
          returnData: true,
        });

        let records = [];
        if (Array.isArray(res)) records = res;
        else if (res && Array.isArray(res.data)) records = res.data;

        // Ensure summary exists on every record
        this.records = records
          .filter((r) => r.summary)
          .map((r) => ({
            ...r,
            summary: { ...r.summary },
          }));

        // Pre-fill totalDays from first record if already saved
        if (this.records.length > 0) {
          const maxTotal = Math.max(
            ...this.records.map((r) => r.summary.totalDays || 0)
          );
          this.totalDays = maxTotal > 0 ? maxTotal : this.daysInMonth;
        }

        this.$nextTick(() => this.updateScrollbarWidth());
      } catch (e) {
        console.error("Load attendance error:", e);
        this.$bvToast.toast("Failed to load attendance records.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    // ── Validation helpers ──────────────────────────────────────────────────

    cancelEdit() {
      this.records = [];
      this.totalDays = null;
      this.filters.classID = null;
      this.filters.sectionID = null;
      this.filters.className = "";
      this.filters.sectionName = "";
      this.filters.campusID = this.$store.state.userData.cId;
      this.viewMode = "grid";
      this.loadGrid();
    },

    onTotalDaysChange() {
      if (this.totalDays > this.daysInMonth) {
        this.totalDays = this.daysInMonth;
        this.$bvToast.toast(
          `Total days cannot exceed ${this.daysInMonth} for the selected month.`,
          { title: "Validation", variant: "warning", solid: true }
        );
      }
      // Clamp all present days
      this.records.forEach((r) => this.clampPresentDays(r));
    },

    clampPresentDays(record) {
      const max = this.totalDays || this.daysInMonth;
      if (record.summary.presentDays > max) {
        record.summary.presentDays = max;
      }
      if (record.summary.presentDays < 0) {
        record.summary.presentDays = 0;
      }
    },

    // ── Save ────────────────────────────────────────────────────────────────

    async save() {
      // Parse and validate total days
      const totalDays = parseInt(this.totalDays);
      if (isNaN(totalDays) || totalDays <= 0) {
        this.$bvToast.toast("Please enter total working days.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }
      if (totalDays > this.daysInMonth) {
        this.$bvToast.toast(
          `Total days cannot exceed ${this.daysInMonth} for the selected month.`,
          { title: "Validation", variant: "warning", solid: true }
        );
        return;
      }
      this.totalDays = totalDays;

      // Validate each student's present days
      const invalid = this.records.find(
        (r) => (parseInt(r.summary.presentDays) || 0) > totalDays
      );
      if (invalid) {
        this.$bvToast.toast(
          `Present days for "${invalid.name}" exceeds total working days.`,
          { title: "Validation", variant: "warning", solid: true }
        );
        return;
      }

      // Attach totalDays to every summary with parsed integers
      const body = this.records.map((r) => ({
        ...r.summary,
        totalDays,
        presentDays: parseInt(r.summary.presentDays) || 0,
      }));

      this.saving = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "AttendanceSummary/Save?db=" +
            this.$store.state.userData.db +
            "&userID=" +
            this.$store.state.userData.userID,
          body,
          message: "Attendance saved successfully.",
          context: this,
          token: this.$store.state.userData.token,
        };
        const ok = await this.post(obj);
        if (ok) this.cancelEdit();
      } catch (e) {
        console.error("Save attendance error:", e);
        this.$bvToast.toast("Could not save attendance.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    async deleteAttendance(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to delete this attendance?",
        {
          title: "Confirm Delete",
          variant: "danger",
          okTitle: "Delete",
          cancelTitle: "Cancel",
          okVariant: "danger",
          cancelVariant: "secondary",
          centered: true,
        }
      );
      if (ok) {
        this.removingAttendance = true;
        const res = await this.post({
          url:
            this.$store.state.domain +
            "AttendanceSummary/DeleteSummary?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            item.campusID +
            "&classID=" +
            item.classID +
            "&sectionID=" +
            item.sectionID +
            "&month=" +
            this.filters.month +
            "&year=" +
            this.filters.year,
          context: this,
          message: "Attendance record deleted",
          token: this.$store.state.userData.token,
        });

        this.removingAttendance = false;

        if (res) {
          this.loadGrid();
        }
      }
    },

    // ── Stats ──
    async LoadStats() {
      this.statsLoading = true;

      const res = await this.get({
        url:
          this.$store.state.domain +
          "AttendanceSummary/Stats?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          (this.selectedCampusID || this.$store.state.userData.cId) +
          "&month=" +
          this.filters.month +
          "&year=" +
          this.filters.year,
        token: this.$store.state.userData.token,
      });
      this.statsLoading = false;
      this.statsObj = res?.campusID ? res : null;
    },

    showUnmarkList() {
      if (this.statsObj) {
        if (this.isHeadOffice && this.isHeadOffice.campusID == this.statsObj.campusID) {
          this.LoadCampuswiseStats();
        }
        else {
          if (this.statsObj.unmarkedStds.length > 0) {
            this.statsRecords = this.statsObj.unmarkedStds;
            this.showStatsModal = true; 
          } else {
            this.$bvToast.toast("No records found.", {
              title: "Info",
              variant: "info",
              solid: true,
            });
          }
        }
      }
      
    },

    async LoadCampuswiseStats() {
      this.campusStatsLoading = true;
      this.showCampusStatsModal = true;
      this.campusStatsList = [];
      const res = await this.get({
        url:
          this.$store.state.domain +
          "AttendanceSummary/CampusWiseStats?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          (this.selectedCampusID || this.$store.state.userData.cId) +
          "&month=" +
          this.filters.month +
          "&year=" +
          this.filters.year,
        token: this.$store.state.userData.token,
      });
      this.campusStatsLoading = false;
      this.campusStatsList = Array.isArray(res) ? res : [];
      if (this.campusStatsList.length === 0) {
        this.$bvToast.toast("No records found.", {
          title: "Info",
          variant: "info",
          solid: true,
        });
      }
    },

    exportCampusUnmarkedExcel(campusItem) {
      if (
        !campusItem ||
        !campusItem.unmarkedStds ||
        campusItem.unmarkedStds.length === 0
      ) {
        this.$bvToast.toast("No unmarked student records to export.", {
          title: "Info",
          variant: "info",
          solid: true,
        });
        return;
      }

      const stamp = new Date().toISOString().slice(0, 10);
      const campusTitle = (
        campusItem.branch ||
        campusItem.campusName ||
        "Campus"
      )
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, "_");
      const fname = `Unmarked-Students-${campusTitle}-${this.monthName}-${this.filters.year}-${stamp}.xlsx`;

      const kFederal = "Federal Board Registration No";
      const kStudent = "Student Name";
      const kFather = "Father Name";
      const kClass = "Class";
      const kSection = "Section";
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";

      const expData = campusItem.unmarkedStds.map((item) => {
        const st = item.student || {};
        const det = item.stdDetails || item.studentDetails || {};
        const meta = item.meta || {};

        const className = meta.className || item.className || "";
        const sectionName = meta.sectionName || item.sectionName || "";

        return {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kClass]: className,
          [kSection]: sectionName,
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Unmarked Students");
      XLSX.writeFile(workbook, fname);
    },

    exportAllCampusUnmarkedExcel() {
      const allUnmarked = [];
      (this.campusStatsList || []).forEach((campus) => {
        if (campus.unmarkedStds && Array.isArray(campus.unmarkedStds)) {
          allUnmarked.push(...campus.unmarkedStds);
        }
      });

      if (allUnmarked.length === 0) {
        this.$bvToast.toast("No unmarked student records to export.", {
          title: "Info",
          variant: "info",
          solid: true,
        });
        return;
      }

      const fname = `Unmarked-Students-all-${this.monthName}-${this.filters.year}.xlsx`;

      const kFederal = "Federal Board Registration No";
      const kStudent = "Student Name";
      const kFather = "Father Name";
      const kCampus = "Campus";
      const kClass = "Class";
      const kSection = "Section";
      const kWa = "WhatsApp No";
      const kParentContact = "Parent's Contact";
      const kStudentContact = "Student's Contact";

      const expData = allUnmarked.map((item) => {
        const st = item.student || {};
        const det = item.stdDetails || item.studentDetails || {};
        const meta = item.meta || {};

        return {
          [kFederal]: det.federalRegNo ? String(det.federalRegNo) : "",
          [kStudent]: st.name || "",
          [kFather]: st.father_name || "",
          [kCampus]: meta.campusName || "",
          [kClass]: meta.className || "",
          [kSection]: meta.sectionName || "",
          [kWa]:
            st.contact_father != null && st.contact_father !== ""
              ? String(st.contact_father)
              : "",
          [kParentContact]:
            st.contact_mother != null && st.contact_mother !== ""
              ? String(st.contact_mother)
              : "",
          [kStudentContact]:
            st.contact_student != null && st.contact_student !== ""
              ? String(st.contact_student)
              : "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(expData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "All Unmarked Students");
      XLSX.writeFile(workbook, fname);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem 1.75rem 1.5rem 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: visible;
  z-index: 1;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    width: calc(100% - 1.5rem);
    height: 5px;
    overflow: hidden;
    border-radius: 10px 10px 0 0;
    z-index: 1;
    pointer-events: none;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 5px;
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
      border-radius: 10px 10px 0 0;
    }
  }
}

.table-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0edff;
  margin-bottom: 1rem;

  h5 {
    font-size: 1.25rem;
    font-weight: 800;
    color: #2d1b69;
  }

  small {
    font-size: 0.85rem;
    color: #6b7280;
  }
}

.filter-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
  display: block;
}

.filter-select ::v-deep .vs__dropdown-toggle {
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid #e0d8ff;
  font-size: 0.875rem;
}

.total-days-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f5f2ff 0%, #ede9ff 100%);
  border: 1px solid #ddd6fe;
  border-radius: 12px;
  flex-wrap: wrap;
}

.total-days-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.total-days-input {
  width: 110px;
  height: 36px;
  border-radius: 8px !important;
  border: 1.5px solid #c4b5fd !important;
  font-size: 0.9rem !important;
  text-align: center;
}

.action-btn-filter {
  height: 38px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
}

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
}

.table-scrollbar-top .table-scrollbar-content {
  height: 1px;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

.data-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;
}

.data-table ::v-deep th {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem 0.875rem;
  white-space: nowrap;
  border-bottom: 1px solid #e2e8f0;
}

.data-table ::v-deep td {
  padding: 0.65rem 0.75rem;
  font-size: 0.85rem;
  vertical-align: middle;
}

.present-days-input {
  width: 100px;
  height: 34px;
  border-radius: 8px !important;
  border: 1.5px solid #e0d8ff !important;
  font-size: 0.9rem !important;
  text-align: center;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 0.2rem rgba(124, 93, 250, 0.2) !important;
  }
}

.w-sn {
  width: 48px;
}
.context-bar {
  display: flex;
  align-items: center;
  padding: 0.6rem 1rem;
  background: linear-gradient(135deg, #f5f2ff 0%, #ede9ff 100%);
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  font-size: 0.875rem;
}
.context-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}
.action-btn-table {
  border-radius: 8px;
}
.report-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, #f5f2ff 0%, #ede9ff 100%);
  border: 1px solid #ddd6fe;
  border-radius: 10px;
}

.report-table ::v-deep th.report-status-sno-th {
  min-width: 4.25rem;
  white-space: nowrap;
  vertical-align: middle;
}

.add-panel {
  background: linear-gradient(135deg, #f5f2ff 0%, #ede9ff 100%);
  border: 1.5px dashed #c4b5fd;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.add-panel__body {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
}
.add-panel__field {
  flex: 1;
  min-width: 180px;
  max-width: 280px;
}
.add-panel__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 1px;
}

.stats-perc-card {
  box-shadow: 0 4px 6px 0 rgba(34, 41, 47, 0.15);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }
}
.stats-marked {
  background: #e5fae7;

  &__perc {
    color: #3c4d35;
  }

  &__label {
    color: #6e9176;
    letter-spacing: 1;
  }
}
.stats-unmarked {
  background: #fae5e5;

  &__perc {
    color: #764a47;
  }

  &__label {
    color: #a67c7c;
    letter-spacing: 1;
  }
}
.stats-icon-box {
  width: 35px;
  height: 35px;
}

::v-deep .campus-stats-modal {
  .modal-dialog.campus-stats-dialog {
      max-width: 94% !important;
  width: 94% !important;
  margin-left: auto !important;
  margin-right: auto !important;
    // max-width: 95vw !important;
    // width: 95vw !important;
    // margin: 1.5rem auto !important;

    // @media (min-width: 1200px) {
    //   max-width: 1150px !important;
    //   width: 1150px !important;
    // }

    // @media (min-width: 1400px) {
    //   max-width: 1250px !important;
    //   width: 1250px !important;
    // }
  } 

  .modal-content {
    max-height: 90vh;
    border-radius: 16px;
    // overflow: hidden;
  }

  .modal-body {
    padding: 1.25rem 1.5rem;
    overflow-y: auto;
  }

  .campus-stats-table {
    width: 100% !important;
    min-width: 100% !important;
    margin-top: 0.5rem;

    th {
      white-space: nowrap;
    }
  }
}
</style>
